<template>
  <div class="modal-overlay" @click="$emit('close')">
    <div class="modal-container" @click.stop>
      <!-- Header Premium avec gradient et icône -->
      <div class="modal-header">
        <div class="header-content">
          <div class="header-icon">
            <i class="fas fa-tools"></i>
          </div>
          <div class="header-text">
            <h3>Détails de l'Operations</h3>
            <p class="header-subtitle">{{ getStatusLabel(intervention?.statut) }}</p>
          </div>
        </div>
        <button @click="$emit('close')" class="close-btn">
          <i class="fas fa-times"></i>
        </button>
      </div>

      <div class="modal-body">
        <!-- Carte Statut Principal -->
        <div class="status-hero-card">
          <div class="status-info">
            <div class="status-badge" :class="getStatusClass(intervention?.statut)">
              <span class="status-icon">{{ getStatusIcon(intervention?.statut) }}</span>
              <span class="status-text">{{ intervention?.statut }}</span>
            </div>
            <div class="intervention-id">
              <i class="fas fa-hashtag"></i>
              ID: {{ intervention?._id?.slice(-8) }}
            </div>
          </div>
          <div class="date-time-info">
            <div class="date-main">{{ formatDateLong(intervention?.dateRdv) }}</div>
            <div class="time-range">
              <i class="fas fa-clock"></i>
              {{ intervention?.heureDebut }} - {{ intervention?.heureFin }}
            </div>
            <div class="prix-rdv-badge">
              <i class="fas fa-coins"></i>
              {{ formatPrice(intervention?.prospect?.prix) }}
            </div>
          </div>
        </div>

        <!-- Grille d'informations -->
        <div class="info-grid">
          <!-- Informations Client -->
          <div class="info-card client-card" :class="{ 'section-collapsed': !expandedSections.client }">
            <div class="card-header" @click="toggleSection('client')">
              <div class="card-icon client-icon">
                <i class="fas fa-user"></i>
              </div>
              <h4>Informations Client</h4>
              <i class="fas fa-chevron-down section-toggle-icon"></i>
            </div>
            <div v-show="expandedSections.client" class="card-content">
              <div class="info-row">
                <span class="info-label">Nom complet</span>
                <span class="info-value">{{ intervention?.prospect?.client?.nomComplet || '-' }}</span>
              </div>
              <div class="info-row">
                <span class="info-label">Téléphone</span>
                <span class="info-value">
                  <i class="fas fa-phone"></i>
                  <a :href="`tel:${intervention?.prospect?.client?.telephone}`">{{ intervention?.prospect?.client?.telephone || '-' }}</a>
                </span>
              </div>
              <div class="info-row" v-if="intervention?.prospect?.client?.email">
                <span class="info-label">Email</span>
                <span class="info-value">
                  <i class="fas fa-envelope"></i>
                  <a :href="`mailto:${intervention?.prospect?.client?.email}`">{{ intervention?.prospect?.client?.email }}</a>
                </span>
              </div>
              <div class="info-row">
                <span class="info-label">Adresse</span>
                <div class="info-value address-with-nav">
                  <span>
                    <i class="fas fa-map-marker-alt"></i>
                    {{ formatAddress(intervention?.prospect?.client?.adresse) }}
                  </span>
                  <a :href="getMapsUrl(intervention?.prospect?.client?.adresse)" target="_blank" class="nav-btn">
                    🗺️ Navigation
                  </a>
                </div>
              </div>
            </div>
          </div>

          <!-- Informations Technicien -->
          <div class="info-card technicien-card" :class="{ 'section-collapsed': !expandedSections.technicien }">
            <div class="card-header" @click="toggleSection('technicien')">
              <div class="card-icon technicien-icon">
                <i class="fas fa-hard-hat"></i>
              </div>
              <h4>Technicien Assigné</h4>
              <i class="fas fa-chevron-down section-toggle-icon"></i>
            </div>
            <div v-show="expandedSections.technicien" class="card-content">
              <div class="info-row">
                <span class="info-label">Nom complet</span>
                <span class="info-value">{{ intervention?.technicien?.prenom }} {{ intervention?.technicien?.nom }}</span>
              </div>
              <div class="info-row" v-if="intervention?.technicien?.telephone">
                <span class="info-label">Téléphone</span>
                <span class="info-value">
                  <i class="fas fa-phone"></i>
                  <a :href="`tel:${intervention?.technicien?.telephone}`">{{ intervention?.technicien?.telephone }}</a>
                </span>
              </div>
              <div class="info-row">
                <span class="info-label">Spécialité</span>
                <span class="info-value">
                  <i class="fas fa-wrench"></i>
                  {{ intervention?.technicien?.specialite || 'Installation & Maintenance' }}
                </span>
              </div>
            </div>
          </div>

          <!-- Détails Intervention -->
          <div class="info-card intervention-details-card" :class="{ 'section-collapsed': !expandedSections.intervention }">
            <div class="card-header" @click="toggleSection('intervention')">
              <div class="card-icon intervention-icon">
                <i class="fas fa-tools"></i>
              </div>
              <h4>Détails Operation</h4>
              <i class="fas fa-chevron-down section-toggle-icon"></i>
            </div>
            <div v-show="expandedSections.intervention" class="card-content">
              <div class="info-row">
                <span class="info-label">Type</span>
                <span class="info-value type-badge-value">
                  🔧 {{ intervention?.prospect?.typeIntervention || '-' }}
                </span>
              </div>
              <div class="info-row">
                <span class="info-label">Priorité</span>
                <span class="info-value priority-badge-value" :class="getPriorityClass(intervention?.prospect?.priorite)">
                  {{ getPriorityIcon(intervention?.prospect?.priorite) }} {{ intervention?.prospect?.priorite || 'Normale' }}
                </span>
              </div>
              <div class="info-row" v-if="getAppareilsInfo(intervention?.prospect)">
                <span class="info-label">Appareils</span>
                <span class="info-value appareils-badge-value">
                  📱 {{ getAppareilsInfo(intervention?.prospect) }}
                </span>
              </div>
              <div class="info-row">
                <span class="info-label">Durée</span>
                <span class="info-value">
                  ⏱️ {{ calculateDuration(intervention) }}
                </span>
              </div>
              
              <!-- Éléments à recouvrer (uniquement pour type Recouvrement) -->
              <div v-if="intervention?.prospect?.typeIntervention === 'Recouvrement'" class="recouvrement-elements">
                <div class="info-row recouvrement-title">
                  <span class="info-label">💰 Éléments à recouvrer</span>
                </div>
                <div class="recouvrement-checklist-display">
                  <!-- Abonnement -->
                  <div class="recouvrement-item-display" :class="{ 'item-active': intervention?.prospect?.recouvrementChecklist?.abonnement, 'item-inactive': !intervention?.prospect?.recouvrementChecklist?.abonnement }">
                    <i :class="intervention?.prospect?.recouvrementChecklist?.abonnement ? 'fas fa-check-circle' : 'far fa-circle'"></i>
                    <span>Abonnement</span>
                  </div>
                  <!-- SIM -->
                  <div class="recouvrement-item-display" :class="{ 'item-active': intervention?.prospect?.recouvrementChecklist?.sim, 'item-inactive': !intervention?.prospect?.recouvrementChecklist?.sim }">
                    <i :class="intervention?.prospect?.recouvrementChecklist?.sim ? 'fas fa-check-circle' : 'far fa-circle'"></i>
                    <span>SIM</span>
                  </div>
                  <!-- Tranche Créance -->
                  <div class="recouvrement-item-display" :class="{ 'item-active': intervention?.prospect?.recouvrementChecklist?.trancheCreance, 'item-inactive': !intervention?.prospect?.recouvrementChecklist?.trancheCreance }">
                    <i :class="intervention?.prospect?.recouvrementChecklist?.trancheCreance ? 'fas fa-check-circle' : 'far fa-circle'"></i>
                    <span>Tranche Créance</span>
                  </div>
                </div>
                <!-- Message si aucun élément sélectionné -->
                <div v-if="!intervention?.prospect?.recouvrementChecklist?.abonnement && !intervention?.prospect?.recouvrementChecklist?.sim && !intervention?.prospect?.recouvrementChecklist?.trancheCreance" class="no-recouvrement-items">
                  <i class="fas fa-info-circle"></i>
                  <span>Aucun élément à recouvrer spécifié</span>
                </div>
              </div>
              
              <!-- Photos de l'appareil installé -->
              <div class="photos-appareil-section" v-if="intervention?.photosIntervention && intervention.photosIntervention.length > 0">
                <div class="photos-appareil-header">
                  <i class="fas fa-camera"></i>
                  <span>Photos de l'appareil ({{ intervention.photosIntervention.length }})</span>
                </div>
                <div class="photos-appareil-grid">
                  <div v-for="(photo, index) in intervention.photosIntervention" :key="'photo-' + index" class="photo-appareil-item">
                    <img :src="photo.data" :alt="photo.description || 'Photo appareil'" @click="openPhotoPreview(photo.data)">
                    <span class="photo-type-badge" v-if="photo.type">{{ photo.type }}</span>
                  </div>
                </div>
              </div>
              
              <!-- QR Code du Reçu -->
              <div class="qr-code-section" v-if="intervention?.encaissement">
                <div class="qr-code-header">
                  <i class="fas fa-qrcode"></i>
                  <span>QR Code du Reçu</span>
                </div>
                <div class="qr-code-container">
                  <img v-if="qrCodeDataUrl" :src="qrCodeDataUrl" alt="QR Code Reçu" class="qr-code-image" />
                  <div v-else class="qr-code-loading">
                    <i class="fas fa-spinner fa-spin"></i>
                    <span>Génération...</span>
                  </div>
                </div>
                <p class="qr-code-hint">Scannez pour télécharger le reçu</p>
              </div>
            </div>
          </div>

          <!-- Commercial (si disponible) -->
          <div class="info-card commercial-card" v-if="intervention?.prospect?.commercial" :class="{ 'section-collapsed': !expandedSections.commercial }">
            <div class="card-header" @click="toggleSection('commercial')">
              <div class="card-icon commercial-icon">
                <i class="fas fa-user-tie"></i>
              </div>
              <h4>Commercial</h4>
              <i class="fas fa-chevron-down section-toggle-icon"></i>
            </div>
            <div v-show="expandedSections.commercial" class="card-content">
              <div class="info-row">
                <span class="info-label">Nom complet</span>
                <span class="info-value">{{ intervention?.prospect?.commercial?.prenom }} {{ intervention?.prospect?.commercial?.nom }}</span>
              </div>
              <div class="info-row" v-if="intervention?.prospect?.commercial?.telephone">
                <span class="info-label">Téléphone</span>
                <span class="info-value">
                  <i class="fas fa-phone"></i>
                  <a :href="`tel:${intervention?.prospect?.commercial?.telephone}`">{{ intervention?.prospect?.commercial?.telephone }}</a>
                </span>
              </div>
            </div>
          </div>
        </div>

        <!-- Description -->
        <div class="description-section" v-if="intervention?.prospect?.description">
          <h4><i class="fas fa-file-alt"></i> Description</h4>
          <p>{{ intervention?.prospect?.description }}</p>
        </div>

        <!-- Commentaires -->
        <div class="comments-section" v-if="intervention?.prospect?.commentaires">
          <h4><i class="fas fa-comment-dots"></i> Commentaires</h4>
          <p>{{ intervention?.prospect?.commentaires }}</p>
        </div>

        <!-- Section Check-list pour les interventions de type "Intervention" -->
        <div class="checklist-results-section" 
             v-if="intervention?.prospect?.typeIntervention === 'Intervention' && intervention?.statut === 'Terminé' && ((intervention?.checklistValidation && intervention.checklistValidation.length > 0) || (intervention?.photosIntervention && intervention.photosIntervention.length > 0))">
          <div class="section-header" @click="toggleSection('checklist')">
            <div class="section-icon checklist-icon">
              <i class="fas fa-clipboard-check"></i>
            </div>
            <h4>Résultats de la Check-list</h4>
            <i class="fas fa-chevron-down section-toggle-icon" :class="{ 'rotated': expandedSections.checklist }"></i>
          </div>
          
          <div v-show="expandedSections.checklist" class="checklist-results-content">
            <div class="checklist-items-display" v-if="intervention?.checklistValidation && intervention.checklistValidation.length > 0">
              <div v-for="(item, index) in intervention.checklistValidation" :key="index" 
                   class="checklist-result-item" :class="{ 'completed': item?.completed || item?.valide }">
                <div class="check-icon">
                  <i v-if="item?.completed || item?.valide" class="fas fa-check-circle"></i>
                  <i v-else class="fas fa-times-circle"></i>
                </div>
                <span class="check-label">{{ getChecklistItemLabel(item) }}</span>
              </div>
            </div>
            
            <!-- Photos d'intervention -->
            <div class="photos-results" v-if="intervention?.photosIntervention && intervention.photosIntervention.length > 0">
              <h5><i class="fas fa-camera"></i> Photos d'Intervention</h5>
              
              <!-- Photos Avant -->
              <div class="photos-group" v-if="getPhotosByType('avant').length > 0">
                <h6>📸 Photos Avant</h6>
                <div class="photos-grid">
                  <div v-for="(photo, index) in getPhotosByType('avant')" :key="'avant-' + index" class="photo-item">
                    <img :src="photo.data" alt="Photo avant intervention" @click="openPhotoPreview(photo.data)">
                  </div>
                </div>
              </div>
              
              <!-- Photos Après -->
              <div class="photos-group" v-if="getPhotosByType('apres').length > 0">
                <h6>✅ Photos Après</h6>
                <div class="photos-grid">
                  <div v-for="(photo, index) in getPhotosByType('apres')" :key="'apres-' + index" class="photo-item">
                    <img :src="photo.data" alt="Photo après intervention" @click="openPhotoPreview(photo.data)">
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- Section Actions pour Technicien -->
        <div class="actions-section" v-if="userRole === 'Technicien' && isAssignedTechnicien" :class="{ 'section-collapsed': !expandedSections.actions }">
          <div class="actions-header" @click="toggleSection('actions')">
            <div class="actions-icon">
              <i class="fas fa-play-circle"></i>
            </div>
            <h4>Actions Disponibles</h4>
            <i class="fas fa-chevron-down section-toggle-icon"></i>
          </div>

          <div v-show="expandedSections.actions">
          <!-- Bouton Démarrer (pas pour Recouvrement) -->
          <div v-if="intervention?.statut === 'Planifié' && intervention?.prospect?.typeIntervention !== 'Recouvrement'" class="action-card start-action">
            <div class="action-info">
              <h5>Démarrer l'Intervention</h5>
              <p>Marquer le début de votre intervention sur site</p>
            </div>
            <button @click="startIntervention" class="btn-action btn-start">
              <i class="fas fa-play"></i>
              Démarrer
            </button>
          </div>

          <!-- Formulaire de Finalisation -->
          <div v-if="intervention?.statut === 'En cours'" class="action-card complete-action">
            <div class="action-info">
              <h5>Finaliser l'Intervention</h5>
              <p>Renseigner les informations pour terminer</p>
            </div>
            <div class="complete-form">
              <div class="form-group">
                <label for="imei-input">
                  <i class="fas fa-mobile-alt"></i>
                  IMEI/ICCID Installé *
                </label>
                <input 
                  id="imei-input"
                  v-model="imeiIccid" 
                  placeholder="Saisir l'IMEI ou ICCID du matériel installé" 
                  required
                  class="form-input"
                >
              </div>
              <div class="form-group">
                <label for="comments-input">
                  <i class="fas fa-comment-alt"></i>
                  Commentaires
                </label>
                <textarea 
                  id="comments-input"
                  v-model="comments" 
                  placeholder="Observations, difficultés rencontrées, recommandations..."
                  rows="4"
                  class="form-textarea"
                ></textarea>
              </div>
              <button 
                @click="completeIntervention" 
                :disabled="!imeiIccid.trim()"
                class="btn-action btn-complete"
              >
                <i class="fas fa-check-circle"></i>
                Terminer l'Operation
              </button>
            </div>
          </div>

          <!-- État Terminé -->
          <div v-if="intervention?.statut === 'Terminé'" class="action-card completed-action">
            <div class="completed-info">
              <div class="completed-icon">
                <i class="fas fa-check-circle"></i>
              </div>
              <div class="completed-text">
                <h5>Intervention Terminée</h5>
                <p>Cette intervention a été finalisée avec succès</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Footer avec boutons -->
      <div class="modal-footer">
        <button @click="$emit('close')" class="btn-secondary">
          <i class="fas fa-times"></i>
          Fermer
        </button>
        
        <!-- Bouton Navigation -->
        <a v-if="intervention?.prospect?.client?.adresse" 
           :href="getMapsUrl(intervention.prospect.client.adresse)" 
           target="_blank" 
           class="btn-navigation">
          <i class="fas fa-map-marked-alt"></i>
          Navigation
        </a>
        
        <!-- Bouton Rapport (visible si intervention Terminée) -->
        <button v-if="intervention?.statut === 'Terminé'" 
                @click="viewReport" 
                class="btn-report">
          <i class="fas fa-file-pdf"></i>
          Reçu
        </button>
      </div>
    </div>
  </div>
</template>

<script>
import { ref, computed } from 'vue'
import { useToast } from 'vue-toastification'
import jsPDF from 'jspdf'
import QRCode from 'qrcode'
import { RECU_BASE_URL } from '@/config/api'
import { Filesystem, Directory } from '@capacitor/filesystem'
import { Capacitor } from '@capacitor/core'

// Fonction utilitaire pour télécharger PDF sur mobile
async function downloadPDFMobile(doc, fileName) {
  try {
    const isNative = Capacitor.isNativePlatform()
    if (isNative) {
      const base64Data = doc.output('datauristring').split(',')[1]
      const result = await Filesystem.writeFile({
        path: fileName,
        data: base64Data,
        directory: Directory.Documents,
        recursive: true
      })
      return { success: true, message: `Reçu téléchargé dans Documents/${fileName}`, path: result.uri }
    } else {
      doc.save(fileName)
      return { success: true, message: 'Reçu téléchargé avec succès' }
    }
  } catch (error) {
    console.error('Erreur téléchargement PDF:', error)
    try {
      const pdfBlob = doc.output('blob')
      const blobUrl = URL.createObjectURL(pdfBlob)
      const link = document.createElement('a')
      link.href = blobUrl
      link.download = fileName
      document.body.appendChild(link)
      link.click()
      document.body.removeChild(link)
      setTimeout(() => URL.revokeObjectURL(blobUrl), 5000)
      return { success: true, message: 'Reçu téléchargé' }
    } catch (e) {
      return { success: false, message: 'Erreur lors du téléchargement' }
    }
  }
}

export default {
  name: 'InterventionDetailModal',
  props: { intervention: Object, userRole: String, userId: String },
  emits: ['close', 'intervention-updated'],
  setup(props, { emit }) {
    const toast = useToast()
    const imeiIccid = ref('')
    const comments = ref('')

    // État des sections repliables (toutes fermées par défaut)
    const expandedSections = ref({
      client: false,
      technicien: false,
      intervention: false,
      commercial: false,
      actions: false,
      checklist: false // Fermée par défaut
    })
    
    // Photo preview
    const photoPreviewUrl = ref(null)
    
    // QR Code
    const qrCodeDataUrl = ref(null)
    
    // Générer le QR Code au chargement
    const generateQRCode = async () => {
      if (props.intervention?._id) {
        try {
          const qrCodeUrl = `${RECU_BASE_URL}/recu/${props.intervention._id}`
          qrCodeDataUrl.value = await QRCode.toDataURL(qrCodeUrl, {
            width: 200,
            margin: 2,
            color: { dark: '#000000', light: '#ffffff' }
          })
        } catch (error) {
          console.error('Erreur génération QR Code:', error)
        }
      }
    }
    
    // Générer le QR code immédiatement
    generateQRCode()

    // Fonction pour basculer l'état d'une section
    const toggleSection = (section) => {
      expandedSections.value[section] = !expandedSections.value[section]
    }

    const isAssignedTechnicien = computed(() => 
      props.intervention?.technicien?._id === props.userId
    )

    const formatDate = (date) => new Date(date).toLocaleDateString('fr-FR')
    
    const formatDateLong = (date) => {
      return new Date(date).toLocaleDateString('fr-FR', {
        weekday: 'long',
        year: 'numeric',
        month: 'long',
        day: 'numeric'
      })
    }

    const formatAddress = (adresse) => {
      if (!adresse) return 'Non renseignée'
      
      // Si l'adresse est un objet
      if (typeof adresse === 'object') {
        const parts = []
        if (adresse.rue) parts.push(adresse.rue)
        if (adresse.ville) parts.push(adresse.ville)
        if (adresse.codePostal) parts.push(adresse.codePostal)
        if (adresse.region) parts.push(adresse.region)
        return parts.length > 0 ? parts.join(', ') : 'Non renseignée'
      }
      
      // Si l'adresse est déjà une chaîne
      return adresse
    }

    const getStatusLabel = (statut) => {
      const labels = {
        'Planifié': 'Intervention programmée',
        'Arrivé': 'Technicien sur site',
        'En cours': 'Intervention en cours',
        'Pause': 'Intervention en pause',
        'Terminé': 'Intervention terminée'
      }
      return labels[statut] || 'Statut inconnu'
    }

    const getStatusClass = (statut) => {
      const classes = {
        'Planifié': 'status-planned',
        'Arrivé': 'status-arrived',
        'En cours': 'status-progress',
        'Pause': 'status-pause',
        'Terminé': 'status-completed'
      }
      return classes[statut] || 'status-default'
    }
    
    const getStatusIcon = (statut) => {
      const icons = {
        'Planifié': '📅',
        'Arrivé': '🚗',
        'En cours': '⚡',
        'Pause': '⏸️',
        'Terminé': '✅'
      }
      return icons[statut] || '📋'
    }

    const getPriorityClass = (priorite) => {
      const classes = {
        'Haute': 'priority-high',
        'Normale': 'priority-normal',
        'Basse': 'priority-low'
      }
      return classes[priorite] || 'priority-normal'
    }

    const getPriorityIcon = (priorite) => {
      const icons = {
        'Haute': '🔴',
        'Normale': '🟡',
        'Basse': '🟢'
      }
      return icons[priorite] || '🟡'
    }

    const getMapsUrl = (adresse) => {
      if (!adresse) return '#'
      const query = `${adresse.rue || ''}, ${adresse.ville || ''}, ${adresse.codePostal || ''}`
      return `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(query)}`
    }

    const getAppareilsInfo = (prospect) => {
      if (!prospect) return null
      
      if (prospect.categories && prospect.categories.length > 0) {
        return prospect.categories
          .filter(cat => cat.quantite > 0)
          .map(cat => `${cat.quantite} ${cat.categorieId?.nom || 'appareil(s)'}`)
          .join(', ')
      }
      
      if (prospect.appareils && prospect.appareils.length > 0) {
        return prospect.appareils
          .filter(app => app.quantite > 0)
          .map(app => `${app.quantite} ${app.type}`)
          .join(', ')
      }
      
      if (prospect.nombreAppareils) {
        return `${prospect.nombreAppareils} appareil(s)`
      }
      
      return null
    }

    const calculateDuration = (intervention) => {
      if (!intervention?.heureDebut || !intervention?.heureFin) return '-'
      const start = new Date(`2000-01-01T${intervention.heureDebut}`)
      const end = new Date(`2000-01-01T${intervention.heureFin}`)
      const diff = (end - start) / (1000 * 60)
      if (diff < 0) return '-'
      return `${Math.floor(diff / 60)}h${diff % 60 > 0 ? Math.floor(diff % 60) + 'm' : ''}`
    }
    
    // Méthodes pour les photos
    const getPhotosByType = (type) => {
      if (!props.intervention?.photosIntervention) return []
      return props.intervention.photosIntervention.filter(photo => photo.type === type)
    }
    
    const openPhotoPreview = (url) => {
      photoPreviewUrl.value = url
      window.open(url, '_blank')
    }
    
    // Méthode pour obtenir le label d'un élément de check-list
    const getChecklistItemLabel = (item) => {
      // Si l'item a un label direct
      if (item && item.label) return item.label
      // Si l'item a un champ item
      if (item && item.item) return item.item
      // Fallback basé sur l'id
      const labelMap = {
        'ancien_boitier': 'Ancien boitier',
        'migration': 'Migration',
        'sim_injoignable': 'SIM injoignable',
        'pas_solde_internet': 'Pas de solde internet',
        'pas_solde_sms': 'Pas de solde SMS',
        'expiration_abonnement': 'Expiration Abonnement',
        'pas_courant_boitier': 'Pas de courant au niveau de boitier',
        'boitier_defectueux': 'Boitier défectueux',
        'login_mot_passe': 'Login ou Mot de passe',
        'probleme_notification': 'Problème notification',
        'manque_evenements': 'Manque d\'événements',
        'absence_historique': 'Absence d\'historique',
        'numero_sos': 'Numéro SOS',
        'autres': 'Autres...',
        // Anciens formats pour compatibilité
        'power': 'Vérification alimentation',
        'cables': 'Contrôle câblage',
        'tests': 'Tests fonctionnels',
        'app': 'Configuration application',
        'coupling': 'Test couplage',
        'materielInstalle': 'Matériel installé',
        'testConnexion': 'Test de connexion',
        'formationClient': 'Formation client',
        'documentationRemise': 'Documentation remise'
      }
      if (item && item.id && labelMap[item.id]) {
        return labelMap[item.id]
      }
      // Si l'item est une chaîne de caractères
      if (typeof item === 'string') {
        return item
      }
      // Dernier fallback
      return item?.id ? `Vérification: ${item.id}` : 'Vérification'
    }

    const startIntervention = async () => {
      try {
        const token = localStorage.getItem('token')
        await fetch(`/api/rdv/${props.intervention._id}/demarrer`, {
          method: 'POST',
          headers: { 'Authorization': `Bearer ${token}` }
        })
        toast.success('✅ Intervention démarrée')
        emit('intervention-updated')
        emit('close')
      } catch (error) {
        toast.error('Erreur')
      }
    }

    const completeIntervention = async () => {
      try {
        const token = localStorage.getItem('token')
        await fetch(`/api/rdv/${props.intervention._id}/cloturer`, {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({
            checklist: {
              materielInstalle: true,
              testConnexion: true,
              formationClient: true,
              documentationRemise: true
            },
            imeiIccid: imeiIccid.value,
            commentaires: comments.value
          })
        })
        toast.success('✅ Intervention terminée')
        emit('intervention-updated')
        emit('close')
      } catch (error) {
        toast.error('Erreur')
      }
    }

    const viewReport = async () => {
      try {
        const intervention = props.intervention
        console.log('🔍 Debug - Intervention complète:', intervention)
        
        toast.info('📄 Génération du rapport en cours...')
        
        const recuId = intervention._id?.substring(intervention._id.length - 8).toUpperCase() || 'XXXXXXXX'
        
        // Générer le QR Code avec l'URL du reçu (utiliser le domaine de production)
        const qrCodeUrl = `${RECU_BASE_URL}/recu/${intervention._id}`
        let qrCodeDataUrl = null
        try {
          qrCodeDataUrl = await QRCode.toDataURL(qrCodeUrl, {
            width: 100,
            margin: 1,
            color: {
              dark: '#000000',
              light: '#ffffff'
            },
            errorCorrectionLevel: 'M'
          })
        } catch (qrError) {
          console.warn('⚠️ Erreur génération QR Code:', qrError)
        }
        
        // Calculer la hauteur exacte du contenu
        let contentHeight = 10 // Marge initiale y=10
        contentHeight += 5 + 4 + 4 + 4 + 6 // Header AMANA (5+4+4+4+6=23)
        contentHeight += 6 // Date
        contentHeight += 6 // Ligne séparation + espace
        contentHeight += 6 + 6 // Titre REÇU + Numéro
        contentHeight += 6 // Ligne séparation
        contentHeight += 4 // Demandeur
        
        // Adresse client pour calcul hauteur
        let hasClientAddress = false
        if (intervention.prospect?.client?.adresse) {
          const addr = intervention.prospect.client.adresse
          if (addr.rue || addr.ville || addr.codePostal || addr.region) {
            hasClientAddress = true
          }
        }
        if (hasClientAddress) contentHeight += 4
        
        contentHeight += 6 // Ligne séparation
        
        // Appareils
        let appareilsCount = 0
        if (intervention.prospect?.categories?.length > 0) {
          appareilsCount = intervention.prospect.categories.length
        } else if (intervention.prospect?.appareils?.length > 0) {
          appareilsCount = intervention.prospect.appareils.length
        } else {
          appareilsCount = 1
        }
        contentHeight += appareilsCount * 4 + 2 // Appareils
        
        contentHeight += 4 + 4 + 6 // Technicien + Paiement + espace
        contentHeight += 1 + 1 + 6 // Double ligne + espace
        contentHeight += 6 // Total
        contentHeight += 1 + 1 + 8 // Double ligne + espace
        contentHeight += 4 + 4 + 8 // Footer texte
        contentHeight += 5 // Barcode
        contentHeight += 5 // Numéro sous barcode
        contentHeight += 8 // Espace avant QR code
        contentHeight += 25 + 3 + 5 // QR code (25) + espace (3) + texte (5)
        
        const finalHeight = contentHeight + 5 // Marge finale
        
        const doc = new jsPDF({
          orientation: 'portrait',
          unit: 'mm',
          format: [80, finalHeight]
        })
        
        const pageWidth = 80
        const centerX = pageWidth / 2
        let y = 10

        // ========== LOGO AMANA EN HAUT À DROITE ==========
        const logoSize = 18
        const logoX = pageWidth - logoSize - 5
        const logoY = 5
        try {
          const logoImg = new Image()
          logoImg.src = require('@/assets/amana1.jpeg')
          doc.addImage(logoImg, 'JPEG', logoX, logoY, logoSize, logoSize)
        } catch (e) {
          console.log('Logo non chargé:', e)
        }

        // ========== HEADER AMANA ==========
        doc.setFontSize(14)
        doc.setFont('helvetica', 'bold')
        doc.setTextColor(0, 0, 0)
        doc.text('AMANA GPS', 8, y) // Aligné à gauche pour laisser place au QR
        y += 5
        
        doc.setFontSize(8)
        doc.setFont('helvetica', 'normal')
        doc.text('9 AV Bardo Hbib,Bourgiba Tunis', 8, y)
        y += 4
        doc.text('Tel: +216 96 677 399', 8, y)
        y += 6

        // Date
        doc.setFontSize(8)
        doc.setFont('helvetica', 'normal')
        doc.text(`Date: ${new Date().toLocaleDateString('fr-FR')}`, 8, y)
        y += 6

        // Ligne de séparation
        doc.setLineWidth(0.3)
        doc.line(8, y, pageWidth - 8, y)
        y += 6

        // ========== TITRE ==========
        doc.setFontSize(11)
        doc.setFont('helvetica', 'bold')
        doc.text('REÇU DE CAISSE', centerX, y, { align: 'center' })
        y += 6

        // Numéro en bas du titre
        doc.setFontSize(8)
        doc.setFont('helvetica', 'normal')
        doc.text(`N° ${recuId}`, centerX, y, { align: 'center' })
        y += 6

        // Ligne de séparation
        doc.setLineWidth(0.3)
        doc.line(8, y, pageWidth - 8, y)
        y += 6

        // Demandeur (Client)
        let clientFullName = 'Non renseigné'
        let clientAddress = ''
        let clientTelephone = ''
        let clientCodeLog = ''
        if (intervention.prospect?.client) {
          const client = intervention.prospect.client
          if (client.nomComplet) {
            clientFullName = client.nomComplet
          } else if (client.nom || client.prenom) {
            clientFullName = `${client.nom || ''} ${client.prenom || ''}`.trim()
          }
          
          // Récupérer le téléphone
          if (client.telephone) {
            clientTelephone = client.telephone
          }
          
          // Récupérer le code log
          clientCodeLog = client.codeLog || intervention.codeLog || ''
          
          // Formater l'adresse
          if (client.adresse) {
            const addr = client.adresse
            const parts = []
            if (addr.rue) parts.push(addr.rue)
            if (addr.ville) parts.push(addr.ville)
            if (addr.codePostal) parts.push(addr.codePostal)
            if (addr.region) parts.push(addr.region)
            clientAddress = parts.join(', ')
          }
        }
        
        doc.setFontSize(8)
        doc.setFont('helvetica', 'normal')
        doc.text(`Demandeur: ${clientFullName}`, 8, y)
        y += 4
        if (clientTelephone) {
          doc.text(`Tél: ${clientTelephone}`, 8, y)
          y += 4
        }
          if (clientAddress) {
          doc.text(`Adresse: ${clientAddress}`, 8, y)
          y += 4
        }
        if (clientCodeLog) {
          doc.text(`Login: ${clientCodeLog}`, 8, y)
          y += 4
        }
      

        // Ligne de séparation
        doc.setLineWidth(0.3)
        doc.line(8, y, pageWidth - 8, y)
        y += 6

        // Type d'intervention avec appareils
        const typeIntervention = intervention.prospect?.typeIntervention || 'Installation'

        // Récupérer les appareils
        let appareilsList = []
        if (intervention.prospect?.categories && intervention.prospect.categories.length > 0) {
          intervention.prospect.categories.forEach(cat => {
            if (cat.nom && cat.quantite) {
              appareilsList.push({
                nom: cat.nom,
                quantite: cat.quantite
              })
            } else if (cat.categorieId?.nom && cat.quantite) {
              appareilsList.push({
                nom: cat.categorieId.nom,
                quantite: cat.quantite
              })
            }
          })
        } else if (intervention.prospect?.appareils && intervention.prospect.appareils.length > 0) {
          intervention.prospect.appareils.forEach(app => {
            if (app.type && app.quantite) {
              appareilsList.push({
                nom: app.type,
                quantite: app.quantite
              })
            }
          })
        } else if (intervention.prospect?.typeAppareil) {
          appareilsList.push({
            nom: intervention.prospect.typeAppareil,
            quantite: intervention.prospect.nombreAppareils || 1
          })
        }

        // Afficher Type d'intervention avec appareils
        if (appareilsList.length > 0) {
          appareilsList.forEach(appareil => {
            doc.setFontSize(8)
            doc.setFont('helvetica', 'bold')
            doc.text(`${typeIntervention}: ${appareil.nom} (${appareil.quantite})`, 8, y)
            y += 4
          })
        } else {
          doc.setFontSize(8)
          doc.setFont('helvetica', 'bold')
          doc.text(`${typeIntervention}: Service`, 8, y)
          y += 4
        }
        y += 2

        // Fournisseur (Technicien)
        let technicienFullName = 'Non assigné'
        if (intervention.technicien) {
          technicienFullName = `${intervention.technicien.prenom || ''} ${intervention.technicien.nom || ''}`.trim() || 'Non assigné'
        }
        
        doc.setFontSize(8)
        doc.setFont('helvetica', 'normal')
        doc.text(`Technicien: ${technicienFullName}`, 8, y)
        y += 4
          
        // Mode de paiement
        const modePaiement = intervention.encaissement?.modePaiement || 'Espèces'
        const modePaiementLabels = {
          'especes': 'Espèces',
          'Espèces': 'Espèces',
          'cheque': 'Chèque',
          'Chèque': 'Chèque',
          'carte_bancaire': 'Carte bancaire',
          'CB': 'Carte bancaire',
          'virement': 'Virement',
          'Virement': 'Virement',
          'prelevement': 'Prélèvement',
          'Prélèvement': 'Prélèvement'
        }
        doc.text(`Paiement: ${modePaiementLabels[modePaiement] || modePaiement}`, 8, y)
        y += 6

        // Ligne de séparation double
        doc.setLineWidth(0.5)
        doc.line(8, y, pageWidth - 8, y)
        y += 1
        doc.line(8, y, pageWidth - 8, y)
        y += 6

        // TOTAL
        const montantTotal = intervention.encaissement?.montant || intervention.prospect?.prix || 0
        const montantFormate = new Intl.NumberFormat('fr-FR', { 
          minimumFractionDigits: 0,
          maximumFractionDigits: 0 
        }).format(montantTotal)
        
        doc.setFontSize(10)
        doc.setFont('helvetica', 'bold')
        doc.text('TOTAL:', 8, y)
        doc.text(`${montantFormate} TND`, pageWidth - 8, y, { align: 'right' })
        y += 6

        // Ligne de séparation double
        doc.setLineWidth(0.5)
        doc.line(8, y, pageWidth - 8, y)
        y += 1
        doc.line(8, y, pageWidth - 8, y)
        y += 8

        // Footer
        doc.setFontSize(7)
        doc.setFont('helvetica', 'italic')
        doc.setTextColor(100, 100, 100)
        doc.text('Merci de votre confiance', centerX, y, { align: 'center' })
        y += 8

        // Barcode simulé
        doc.setFontSize(20)
        doc.setFont('helvetica', 'normal')
        doc.setTextColor(0, 0, 0)
        const barcodeText = '| || ||| || | || || | ||| || ||| ||| ||'
        doc.text(barcodeText, centerX, y, { align: 'center' })
        y += 5
        doc.setFontSize(8)
        doc.text(recuId, centerX, y, { align: 'center' })
        y += 8
        
        // QR Code en bas du reçu
        if (qrCodeDataUrl) {
          const qrSize = 25
          const qrX = (pageWidth - qrSize) / 2
          doc.addImage(qrCodeDataUrl, 'PNG', qrX, y, qrSize, qrSize)
          y += qrSize + 3
          doc.setFontSize(6)
          doc.setTextColor(100, 100, 100)
          doc.text('Scannez pour télécharger le reçu', centerX, y, { align: 'center' })
        }

        // Télécharger le PDF avec Capacitor Filesystem (compatible mobile)
        const fileName = `recu_caisse_${recuId}.pdf`
        const result = await downloadPDFMobile(doc, fileName)
        
        if (result.success) {
          toast.success(`✅ ${result.message}`)
        } else {
          toast.error(result.message)
        }
      } catch (error) {
        console.error('❌ Erreur génération rapport:', error)
        toast.error('Erreur lors de la génération du rapport')
      }
    }

    const formatPrice = (price) => {
      if (price === null || price === undefined) return 'Non renseigné'
      return `${Number(price).toLocaleString('fr-FR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} TND`
    }

    return {
      imeiIccid, comments, isAssignedTechnicien, 
      formatDate, formatDateLong, formatAddress, formatPrice, getStatusLabel, getStatusClass, getStatusIcon,
      getPriorityClass, getPriorityIcon, getMapsUrl, getAppareilsInfo, calculateDuration,
      startIntervention, completeIntervention, viewReport,
      expandedSections, toggleSection,
      // Méthodes pour les photos et checklist
      getPhotosByType, openPhotoPreview, photoPreviewUrl, getChecklistItemLabel,
      // QR Code
      qrCodeDataUrl
    }
  }
}
</script>

<style scoped>
/* Variables CSS pour cohérence */
:root {
  --primary-blue: #001bb7;
  --secondary-blue: #0046ff;
  --success-green: #10b981;
  --warning-orange: #f59e0b;
  --danger-red: #ef4444;
  --gray-50: #f9fafb;
  --gray-100: #f3f4f6;
  --gray-200: #e5e7eb;
  --gray-300: #d1d5db;
  --gray-400: #9ca3af;
  --gray-500: #6b7280;
  --gray-600: #4b5563;
  --gray-700: #374151;
  --gray-800: #1f2937;
  --gray-900: #111827;
}

/* Modal Overlay */
.modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.75);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
  backdrop-filter: blur(8px);
  animation: fadeIn 0.3s ease-out;
}

@keyframes fadeIn {
  from { opacity: 0; }
  to { opacity: 1; }
}

/* Modal Container */
.modal-container {
  background: white;
  border-radius: 24px;
  width: 95%;
  max-width: 900px;
  max-height: 95vh;
  overflow-y: auto;
  box-shadow: 0 25px 80px rgba(0, 0, 0, 0.3);
  animation: slideUp 0.4s cubic-bezier(0.34, 1.56, 0.64, 1);
}

@keyframes slideUp {
  from {
    opacity: 0;
    transform: translateY(40px) scale(0.95);
  }
  to {
    opacity: 1;
    transform: translateY(0) scale(1);
  }
}

/* Header Premium */
.modal-header {
  background: linear-gradient(135deg, var(--primary-blue), var(--secondary-blue));
  color: white;
  padding: 2rem;
  border-radius: 24px 24px 0 0;
  position: relative;
  overflow: hidden;
}

.modal-header::before {
  content: '';
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: linear-gradient(45deg, rgba(255,255,255,0.1) 0%, transparent 50%);
  pointer-events: none;
}

.header-content {
  display: flex;
  align-items: center;
  gap: 1.5rem;
  position: relative;
  z-index: 1;
}

.header-icon {
  width: 64px;
  height: 64px;
  background: rgba(255, 255, 255, 0.2);
  border-radius: 20px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 1.8rem;
  backdrop-filter: blur(10px);
  border: 1px solid rgba(255, 255, 255, 0.3);
}

.header-text h3 {
  margin: 0 0 0.5rem 0;
  font-size: 1.8rem;
  font-weight: 700;
  letter-spacing: -0.02em;
}

.header-subtitle {
  margin: 0;
  opacity: 0.9;
  font-size: 1rem;
  font-weight: 500;
}

.close-btn {
  position: absolute;
  top: 1.5rem;
  right: 1.5rem;
  width: 48px;
  height: 48px;
  background: rgba(255, 255, 255, 0.2);
  border: none;
  border-radius: 16px;
  color: white;
  font-size: 1.2rem;
  cursor: pointer;
  transition: all 0.3s ease;
  backdrop-filter: blur(10px);
  border: 1px solid rgba(255, 255, 255, 0.3);
  z-index: 2;
}

.close-btn:hover {
  background: rgba(255, 255, 255, 0.3);
  transform: scale(1.05);
}

/* Modal Body */
.modal-body {
  padding: 2rem;
}

/* Status Hero Card */
.status-hero-card {
  background: linear-gradient(135deg, rgba(0, 27, 183, 0.05), rgba(0, 70, 255, 0.03));
  border-radius: 20px;
  padding: 2rem;
  margin-bottom: 2rem;
  display: flex;
  justify-content: space-between;
  align-items: center;
  border: 1px solid rgba(0, 27, 183, 0.1);
  position: relative;
  overflow: hidden;
}

.status-hero-card::before {
  content: '';
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: linear-gradient(45deg, rgba(255,255,255,0.5) 0%, transparent 50%);
  pointer-events: none;
}

.status-info {
  position: relative;
  z-index: 1;
}

.status-badge {
  display: inline-flex;
  align-items: center;
  gap: 0.75rem;
  padding: 0.75rem 1.5rem;
  border-radius: 16px;
  font-weight: 600;
  font-size: 1rem;
  margin-bottom: 1rem;
  border: 2px solid transparent;
}

.status-badge.status-planned {
  background: linear-gradient(135deg, #3b82f6, #1d4ed8);
  color: white;
}

.status-badge.status-arrived {
  background: linear-gradient(135deg, #f59e0b, #d97706);
  color: white;
}

.status-badge.status-progress {
  background: linear-gradient(135deg, #10b981, #059669);
  color: white;
}

.status-badge.status-pause {
  background: linear-gradient(135deg, #6b7280, #4b5563);
  color: white;
}

.status-badge.status-completed {
  background: linear-gradient(135deg, #10b981, #059669);
  color: white;
}

.status-icon {
  font-size: 1.2rem;
}

.intervention-id {
  color: var(--gray-600);
  font-weight: 600;
  font-size: 0.9rem;
  display: flex;
  align-items: center;
  gap: 0.5rem;
}

.date-time-info {
  text-align: right;
  position: relative;
  z-index: 1;
}

.date-main {
  font-size: 1.2rem;
  font-weight: 700;
  color: var(--primary-blue);
  margin-bottom: 0.5rem;
  text-transform: capitalize;
}

.time-range {
  color: var(--gray-600);
  font-weight: 600;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  justify-content: flex-end;
  margin-bottom: 0.75rem;
}

.prix-rdv-badge {
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.75rem 1.25rem;
  background: linear-gradient(135deg, rgba(16, 185, 129, 0.15), rgba(5, 150, 105, 0.1));
  border: 2px solid rgba(16, 185, 129, 0.3);
  border-radius: 12px;
  font-size: 1.1rem;
  font-weight: 700;
  color: #059669;
  box-shadow: 0 2px 8px rgba(16, 185, 129, 0.15);
  transition: all 0.3s ease;
  justify-content: flex-end;
}

.prix-rdv-badge:hover {
  transform: scale(1.05);
  box-shadow: 0 4px 12px rgba(16, 185, 129, 0.25);
}

.prix-rdv-badge i {
  color: #10b981;
  font-size: 1rem;
}

/* Info Grid */
.info-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(400px, 1fr));
  gap: 1.5rem;
  margin-bottom: 2rem;
}

.info-card {
  background: rgba(248, 250, 252, 0.8);
  border-radius: 20px;
  border: 1px solid rgba(0, 27, 183, 0.08);
  overflow: hidden;
  transition: all 0.3s ease;
  backdrop-filter: blur(10px);
}

.info-card:hover {
  transform: translateY(-4px);
  box-shadow: 0 12px 40px rgba(0, 27, 183, 0.15);
  border-color: rgba(0, 27, 183, 0.2);
}

.card-header {
  background: linear-gradient(135deg, rgba(0, 27, 183, 0.08), rgba(0, 70, 255, 0.05));
  padding: 1.5rem;
  display: flex;
  align-items: center;
  gap: 1rem;
  border-bottom: 1px solid rgba(0, 27, 183, 0.1);
  cursor: pointer;
  user-select: none;
  position: relative;
}

.card-icon {
  width: 48px;
  height: 48px;
  border-radius: 16px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 1.2rem;
  color: white;
  font-weight: 600;
}

.client-icon {
  background: linear-gradient(135deg, #3b82f6, #1d4ed8);
}

.technicien-icon {
  background: linear-gradient(135deg, #10b981, #059669);
}

.intervention-icon {
  background: linear-gradient(135deg, #f59e0b, #d97706);
}

.commercial-icon {
  background: linear-gradient(135deg, #8b5cf6, #7c3aed);
}

.card-header h4 {
  margin: 0;
  color: var(--primary-blue);
  font-weight: 700;
  font-size: 1.1rem;
  flex: 1;
}

.section-toggle-icon {
  color: var(--primary-blue);
  font-size: 1rem;
  transition: transform 0.3s ease;
  margin-left: auto;
}

.section-collapsed .section-toggle-icon {
  transform: rotate(-90deg);
}

.card-content {
  overflow: hidden;
  transition: max-height 0.4s ease-out;
}

.card-content {
  padding: 1.5rem;
}

.info-row {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: 1rem;
  gap: 1rem;
}

.info-row:last-child {
  margin-bottom: 0;
}

.info-label {
  font-weight: 600;
  color: var(--gray-600);
  min-width: 100px;
  font-size: 0.9rem;
}

.info-value {
  flex: 1;
  text-align: right;
  color: var(--gray-800);
  font-weight: 500;
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 0.5rem;
}

.info-value a {
  color: var(--primary-blue);
  text-decoration: none;
  font-weight: 600;
}

.info-value a:hover {
  text-decoration: underline;
}

.info-value i {
  color: var(--gray-500);
  font-size: 0.9rem;
}

.info-value.prix-rdv {
  color: #10b981;
  font-weight: 700;
  font-size: 1.1rem;
}

.info-value.prix-rdv i {
  color: #10b981;
}

/* Badges dans les valeurs */
.type-badge-value {
  padding: 0.25rem 0.75rem;
  background: linear-gradient(135deg, rgba(0, 27, 183, 0.1), rgba(0, 70, 255, 0.05));
  border: 1px solid rgba(0, 27, 183, 0.2);
  border-radius: 8px;
  color: var(--primary-blue);
  font-weight: 600;
}

.priority-badge-value {
  padding: 0.25rem 0.75rem;
  border-radius: 8px;
  font-weight: 600;
}

.priority-badge-value.priority-high {
  background: linear-gradient(135deg, #fee2e2, #fef2f2);
  border: 1px solid #ef4444;
  color: #dc2626;
}

.priority-badge-value.priority-normal {
  background: linear-gradient(135deg, #fef3c7, #fffbeb);
  border: 1px solid #f59e0b;
  color: #d97706;
}

.priority-badge-value.priority-low {
  background: linear-gradient(135deg, #d1fae5, #ecfdf5);
  border: 1px solid #10b981;
  color: #059669;
}

.appareils-badge-value {
  padding: 0.25rem 0.75rem;
  background: linear-gradient(135deg, #e0e7ff, #eef2ff);
  border: 1px solid #6366f1;
  border-radius: 8px;
  color: #4f46e5;
  font-weight: 600;
}

/* Adresse avec navigation */
.address-with-nav {
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  gap: 0.5rem;
}

.nav-btn {
  display: inline-flex;
  align-items: center;
  padding: 0.4rem 0.75rem;
  background: linear-gradient(135deg, #10B981, #059669);
  color: white;
  border-radius: 8px;
  font-size: 0.8rem;
  font-weight: 600;
  text-decoration: none;
  transition: all 0.3s ease;
  box-shadow: 0 2px 8px rgba(16, 185, 129, 0.3);
}

.nav-btn:hover {
  transform: translateY(-2px);
  box-shadow: 0 4px 12px rgba(16, 185, 129, 0.4);
  color: white;
}

/* Description et Commentaires */
.description-section, .comments-section {
  background: linear-gradient(135deg, #f8fafc, #f1f5f9);
  border-radius: 16px;
  padding: 1.5rem;
  margin-bottom: 1.5rem;
  border: 1px solid rgba(0, 27, 183, 0.08);
}

.description-section h4, .comments-section h4 {
  margin: 0 0 1rem 0;
  color: var(--primary-blue);
  font-weight: 700;
  font-size: 1rem;
  display: flex;
  align-items: center;
  gap: 0.5rem;
}

.description-section p, .comments-section p {
  margin: 0;
  color: var(--gray-700);
  line-height: 1.6;
  font-size: 0.95rem;
}

/* Actions Section */
.actions-section {
  background: rgba(248, 250, 252, 0.5);
  border-radius: 20px;
  padding: 2rem;
  border: 1px solid rgba(0, 27, 183, 0.08);
}

.actions-header {
  display: flex;
  align-items: center;
  gap: 1rem;
  margin-bottom: 1.5rem;
  cursor: pointer;
  user-select: none;
  position: relative;
}

.actions-icon {
  width: 48px;
  height: 48px;
  background: linear-gradient(135deg, var(--primary-blue), var(--secondary-blue));
  border-radius: 16px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  font-size: 1.2rem;
}

.actions-header h4 {
  margin: 0;
  color: var(--primary-blue);
  font-weight: 700;
  font-size: 1.2rem;
  flex: 1;
}

.actions-section.section-collapsed > div:not(.actions-header) {
  max-height: 0;
  overflow: hidden;
}

.action-card {
  background: white;
  border-radius: 16px;
  padding: 1.5rem;
  border: 1px solid rgba(0, 27, 183, 0.1);
  transition: all 0.3s ease;
}

.action-card:hover {
  box-shadow: 0 8px 25px rgba(0, 27, 183, 0.1);
  transform: translateY(-2px);
}

.start-action {
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.action-info h5 {
  margin: 0 0 0.5rem 0;
  color: var(--gray-800);
  font-weight: 700;
  font-size: 1.1rem;
}

.action-info p {
  margin: 0;
  color: var(--gray-600);
  font-size: 0.9rem;
}

.btn-action {
  padding: 0.75rem 2rem;
  border: none;
  border-radius: 12px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  font-size: 0.95rem;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

.btn-start {
  background: linear-gradient(135deg, var(--success-green), #059669);
  color: white;
}

.btn-complete {
  background: linear-gradient(135deg, var(--primary-blue), var(--secondary-blue));
  color: white;
  width: 100%;
  justify-content: center;
  padding: 1rem 2rem;
  margin-top: 1rem;
}

.btn-action:hover {
  transform: translateY(-2px);
  box-shadow: 0 8px 25px rgba(0, 0, 0, 0.2);
}

.btn-action:disabled {
  opacity: 0.5;
  cursor: not-allowed;
  transform: none;
  box-shadow: none;
}

/* Complete Form */
.complete-form {
  margin-top: 1rem;
}

.form-group {
  margin-bottom: 1.5rem;
}

.form-group label {
  display: block;
  margin-bottom: 0.5rem;
  font-weight: 600;
  color: var(--gray-700);
  font-size: 0.9rem;
  display: flex;
  align-items: center;
  gap: 0.5rem;
}

.form-input, .form-textarea {
  width: 100%;
  padding: 0.875rem 1rem;
  border: 2px solid rgba(0, 27, 183, 0.1);
  border-radius: 12px;
  font-size: 0.95rem;
  transition: all 0.3s ease;
  background: rgba(255, 255, 255, 0.8);
  backdrop-filter: blur(10px);
}

.form-input:focus, .form-textarea:focus {
  outline: none;
  border-color: var(--primary-blue);
  box-shadow: 0 0 0 4px rgba(0, 27, 183, 0.1);
  background: white;
}

.form-textarea {
  resize: vertical;
  min-height: 100px;
}

/* Completed Action */
.completed-action {
  background: linear-gradient(135deg, rgba(16, 185, 129, 0.05), rgba(5, 150, 105, 0.03));
  border-color: rgba(16, 185, 129, 0.2);
}

.completed-info {
  display: flex;
  align-items: center;
  gap: 1rem;
}

.completed-icon {
  width: 48px;
  height: 48px;
  background: linear-gradient(135deg, var(--success-green), #059669);
  border-radius: 16px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  font-size: 1.5rem;
}

.completed-text h5 {
  margin: 0 0 0.25rem 0;
  color: var(--success-green);
  font-weight: 700;
}

.completed-text p {
  margin: 0;
  color: var(--gray-600);
}

/* Modal Footer */
.modal-footer {
  padding: 1.5rem 2rem;
  border-top: 1px solid rgba(0, 27, 183, 0.1);
  display: flex;
  justify-content: flex-end;
  gap: 1rem;
  flex-wrap: wrap;
  background: rgba(248, 250, 252, 0.5);
  border-radius: 0 0 24px 24px;
}

.btn-secondary {
  padding: 0.75rem 1.5rem;
  background: var(--gray-500);
  color: white;
  border: none;
  border-radius: 12px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;
  display: flex;
  align-items: center;
  gap: 0.5rem;
}

.btn-secondary:hover {
  background: var(--gray-600);
  transform: translateY(-2px);
}

/* Bouton Navigation */
.btn-navigation {
  padding: 0.75rem 1.5rem;
  background: linear-gradient(135deg, #10b981, #059669);
  color: white;
  border: none;
  border-radius: 12px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  text-decoration: none;
}

.btn-navigation:hover {
  background: linear-gradient(135deg, #059669, #047857);
  transform: translateY(-2px);
  box-shadow: 0 4px 15px rgba(16, 185, 129, 0.4);
  color: white;
}

/* Bouton Rapport */
.btn-report {
  padding: 0.75rem 1.5rem;
  background: linear-gradient(135deg, #3b82f6, #1d4ed8);
  color: white;
  border: none;
  border-radius: 12px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;
  display: flex;
  align-items: center;
  gap: 0.5rem;
}

.btn-report:hover {
  background: linear-gradient(135deg, #1d4ed8, #1e40af);
  transform: translateY(-2px);
  box-shadow: 0 4px 15px rgba(59, 130, 246, 0.4);
}

/* Responsive Design */
@media (max-width: 768px) {
  .modal-container {
    width: 98%;
    max-height: 98vh;
    border-radius: 16px;
  }
  
  .modal-header {
    padding: 1.5rem;
    border-radius: 16px 16px 0 0;
  }
  
  .header-content {
    flex-direction: column;
    text-align: center;
    gap: 1rem;
  }
  
  .close-btn {
    top: 1rem;
    right: 1rem;
    width: 40px;
    height: 40px;
  }
  
  .modal-body {
    padding: 1.5rem;
  }
  
  .status-hero-card {
    flex-direction: column;
    text-align: center;
    gap: 1rem;
  }
  
  .date-time-info {
    text-align: center;
  }
  
  .info-grid {
    grid-template-columns: 1fr;
  }
  
  .info-row {
    flex-direction: column;
    align-items: flex-start;
    gap: 0.5rem;
  }
  
  .info-value {
    text-align: left;
    justify-content: flex-start;
  }
  
  .start-action {
    flex-direction: column;
    gap: 1rem;
    text-align: center;
  }
  
  .modal-footer {
    padding: 1rem 1.5rem;
  }
}

@media (max-width: 480px) {
  .header-text h3 {
    font-size: 1.4rem;
  }
  
  .status-badge {
    padding: 0.5rem 1rem;
    font-size: 0.9rem;
  }
  
  .date-main {
    font-size: 1rem;
  }
  
  .btn-action {
    padding: 0.625rem 1.5rem;
    font-size: 0.85rem;
  }
}

/* Section Check-list Results */
.checklist-results-section {
  background: rgba(248, 250, 252, 0.5);
  border-radius: 20px;
  padding: 1.5rem;
  margin-top: 1.5rem;
  border: 1px solid rgba(139, 92, 246, 0.2);
}

.checklist-results-section .section-header {
  display: flex;
  align-items: center;
  gap: 1rem;
  cursor: pointer;
  user-select: none;
}

.checklist-results-section .section-icon {
  width: 48px;
  height: 48px;
  background: linear-gradient(135deg, #8b5cf6, #7c3aed);
  border-radius: 16px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  font-size: 1.2rem;
}

.checklist-results-section h4 {
  margin: 0;
  color: #7c3aed;
  font-weight: 700;
  font-size: 1.1rem;
  flex: 1;
}

.checklist-results-section .section-toggle-icon {
  color: #7c3aed;
  transition: transform 0.3s ease;
}

.checklist-results-section .section-toggle-icon.rotated {
  transform: rotate(180deg);
}

.checklist-results-content {
  margin-top: 1.5rem;
}

.checklist-items-display {
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
}

.checklist-result-item {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  padding: 0.75rem 1rem;
  background: white;
  border-radius: 10px;
  border: 1px solid #e2e8f0;
  transition: all 0.2s ease;
}

.checklist-result-item.completed {
  background: #ecfdf5;
  border-color: #10b981;
}

.checklist-result-item .check-icon {
  font-size: 1.2rem;
}

.checklist-result-item.completed .check-icon {
  color: #10b981;
}

.checklist-result-item:not(.completed) .check-icon {
  color: #ef4444;
}

.checklist-result-item .check-label {
  flex: 1;
  font-size: 0.95rem;
  color: #334155;
}

.checklist-result-item.completed .check-label {
  color: #059669;
}

/* Photos Results */
.photos-results {
  margin-top: 1.5rem;
  padding-top: 1.5rem;
  border-top: 1px solid #e2e8f0;
}

.photos-results h5 {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  font-size: 1rem;
  font-weight: 600;
  color: #1e293b;
  margin: 0 0 1rem 0;
}

.photos-group {
  margin-bottom: 1.5rem;
}

.photos-group h6 {
  font-size: 0.9rem;
  font-weight: 600;
  color: #64748b;
  margin: 0 0 0.75rem 0;
}

.photos-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(100px, 1fr));
  gap: 0.75rem;
}

.photos-grid .photo-item {
  aspect-ratio: 1;
  border-radius: 10px;
  overflow: hidden;
  cursor: pointer;
  transition: all 0.2s ease;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
}

.photos-grid .photo-item:hover {
  transform: scale(1.05);
  box-shadow: 0 4px 15px rgba(0, 0, 0, 0.2);
}

.photos-grid .photo-item img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

/* Éléments de recouvrement */
.recouvrement-elements {
  margin-top: 0.75rem;
  padding-top: 0.75rem;
  border-top: 2px dashed #f59e0b;
}

.recouvrement-title {
  border-bottom: none !important;
  padding-bottom: 0.5rem !important;
}

.recouvrement-title .info-label {
  color: #d97706;
  font-weight: 600;
  font-size: 0.9rem;
}

.recouvrement-checklist-display {
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem;
  padding: 0.5rem 0;
}

.recouvrement-item-display {
  display: inline-flex;
  align-items: center;
  gap: 0.375rem;
  padding: 0.375rem 0.75rem;
  border-radius: 20px;
  font-size: 0.8rem;
  font-weight: 600;
  transition: all 0.2s ease;
}

.recouvrement-item-display.item-active {
  background: linear-gradient(135deg, #d1fae5 0%, #a7f3d0 100%);
  border: 1px solid #10b981;
  color: #065f46;
}

.recouvrement-item-display.item-active i {
  color: #10b981;
  font-size: 0.85rem;
}

.recouvrement-item-display.item-inactive {
  background: linear-gradient(135deg, #f3f4f6 0%, #e5e7eb 100%);
  border: 1px solid #d1d5db;
  color: #9ca3af;
}

.recouvrement-item-display.item-inactive i {
  color: #d1d5db;
  font-size: 0.85rem;
}

.no-recouvrement-items {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.5rem 0.75rem;
  background: #fef3c7;
  border: 1px dashed #f59e0b;
  border-radius: 8px;
  font-size: 0.8rem;
  color: #92400e;
  margin-top: 0.5rem;
}

.no-recouvrement-items i {
  color: #f59e0b;
}

/* Photos Appareil Section dans Détails Operation */
.photos-appareil-section {
  margin-top: 1rem;
  padding-top: 1rem;
  border-top: 1px solid #e2e8f0;
}

.photos-appareil-header {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  font-size: 0.9rem;
  font-weight: 600;
  color: #1e293b;
  margin-bottom: 0.75rem;
}

.photos-appareil-header i {
  color: #10b981;
}

.photos-appareil-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(80px, 1fr));
  gap: 0.5rem;
}

.photo-appareil-item {
  position: relative;
  aspect-ratio: 1;
  border-radius: 8px;
  overflow: hidden;
  cursor: pointer;
  transition: all 0.2s ease;
  box-shadow: 0 2px 6px rgba(0, 0, 0, 0.1);
}

.photo-appareil-item:hover {
  transform: scale(1.05);
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.2);
}

.photo-appareil-item img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.photo-type-badge {
  position: absolute;
  bottom: 4px;
  left: 4px;
  background: rgba(0, 0, 0, 0.7);
  color: white;
  font-size: 0.65rem;
  padding: 2px 6px;
  border-radius: 4px;
  text-transform: capitalize;
}

/* QR Code Section */
.qr-code-section {
  margin-top: 1.5rem;
  padding-top: 1rem;
  border-top: 1px solid #e2e8f0;
  text-align: center;
}

.qr-code-header {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
  font-size: 0.9rem;
  font-weight: 600;
  color: #1e293b;
  margin-bottom: 1rem;
}

.qr-code-header i {
  color: #3b82f6;
  font-size: 1.1rem;
}

.qr-code-container {
  display: flex;
  justify-content: center;
  align-items: center;
  padding: 1rem;
  background: white;
  border-radius: 12px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
  margin: 0 auto;
  max-width: 180px;
}

.qr-code-image {
  width: 150px;
  height: 150px;
  border-radius: 8px;
}

.qr-code-loading {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 0.5rem;
  color: #64748b;
  font-size: 0.85rem;
}

.qr-code-loading i {
  font-size: 1.5rem;
  color: #3b82f6;
}

.qr-code-hint {
  margin-top: 0.75rem;
  font-size: 0.8rem;
  color: #64748b;
  font-style: italic;
}
</style>
